"use client";

import { ReactNode } from "react";
import { csx } from "../../../utils";
import "./TextEllipsis.css";

interface TextEllipsisProps {
  /**
   * The content to display within the text ellipsis.
   */
  children: ReactNode;

  /**
   * The maximum width of the text container in any valid CSS unit (px, rem, em, %, etc.).
   * When the container width is less than this value, text will be truncated with an ellipsis.
   */
  maxWidth?: string | number;

  /**
   * Whether to truncate the text to a single line. Default is true.
   * If false, the text will wrap to multiple lines.
   */
  singleLine?: boolean;

  /**
   * The number of lines to show before truncating with an ellipsis.
   * Only applies when singleLine is false.
   */
  lines?: number;

  /**
   * The title attribute to show on hover. If not provided and singleLine is true,
   * the children will be used as the title (if children is a string).
   */
  title?: string;

  /**
   * A test ID.
   */
  "data-testid"?: string;
}

/**
 * A component that displays text with an ellipsis when it overflows its container.
 * @interface TextEllipsisProps
 */
const TextEllipsis = ({
  children,
  maxWidth,
  singleLine = true,
  lines = 1,
  title,
  "data-testid": testId,
}: TextEllipsisProps) => {
  return (
    <div
      className={csx(
        "proton-TextEllipsis",
        singleLine || lines === 1
          ? "proton-TextEllipsis--single-line"
          : lines > 1
            ? "proton-TextEllipsis--multi-line"
            : ""
      )}
      style={{
        maxWidth: maxWidth,
        WebkitLineClamp: lines,
      }}
      title={title}
      data-testid={testId}
    >
      {children}
    </div>
  );
};

export default TextEllipsis;
